"use client";

import { Avatar } from "@heroui/avatar";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
} from "@heroui/dropdown";
import { addToast } from "@heroui/toast";
import { usePathname } from "next/navigation";
import { useState } from "react";

import { getUserDisplayName, useAuth } from "@/lib/hooks/useAuth";
import { useLibrary } from "@/lib/hooks/useLibrary";
import { getLanguageFromPathname } from "@/lib/i18n/paths";
import { createTranslator } from "@/lib/i18n/translations";
import { setLanguageCookie } from "@/lib/languageCookie";

import SignInModal from "./SignInModal";

/**
 * Fixed-width slot that fits the skeleton, the "Sign In" button (in every
 * language) and the avatar, so neighbors never move when auth resolves.
 */
function AuthSlot({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-11 w-24 shrink-0 items-center justify-end">
      {children}
    </div>
  );
}

function Count({ value }: { value: number | null }) {
  if (value === null) return null;

  return <span className="text-xs tabular-nums text-default-400">{value}</span>;
}

/** Sign-in button, or the signed-in user's menu. */
export function AuthButton() {
  const pathname = usePathname();
  const lang = getLanguageFromPathname(pathname);
  const t = createTranslator(lang);
  const { status, user, signOut } = useAuth();
  const library = useLibrary();
  const [isSignInOpen, setSignInOpen] = useState(false);

  if (status === "loading") {
    return (
      <AuthSlot>
        <div
          aria-hidden="true"
          className="mr-1.5 h-8 w-8 animate-pulse rounded-full bg-default-200"
        />
      </AuthSlot>
    );
  }

  if (!user) {
    return (
      <AuthSlot>
        <SignInModal
          buttonClassName="h-11 min-w-0 px-3 sm:h-10"
          currentLang={lang}
          isOpen={isSignInOpen}
          redirectPath={pathname}
          onOpenChange={setSignInOpen}
        />
      </AuthSlot>
    );
  }

  const name = getUserDisplayName(user);
  const email = user.email || null;
  const metadata = user.user_metadata ?? {};
  const avatarUrl =
    typeof metadata.avatar_url === "string"
      ? metadata.avatar_url
      : typeof metadata.picture === "string"
        ? metadata.picture
        : undefined;
  const ready = library.status === "ready";
  const count = (collection: Record<string, unknown>) =>
    ready ? Object.keys(collection).length : null;

  const handleSignOut = async () => {
    // Keep the language after signing out.
    setLanguageCookie(lang);

    const { ok } = await signOut();

    if (!ok) {
      addToast({
        title: t("userPages.actions.signOutFailed"),
        color: "danger",
      });
    }
  };

  return (
    <AuthSlot>
      <Dropdown placement="bottom-end">
        <DropdownTrigger>
          {/* 44px tap target around the 32px avatar. The avatar shows the
              user's initials or photo: masked in analytics session replays. */}
          <button
            aria-label={t("userPages.actions.accountMenu")}
            className="flex h-11 w-11 items-center justify-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-focus"
            data-dd-privacy="mask"
            type="button"
          >
            <Avatar
              isBordered
              showFallback
              className="pointer-events-none"
              name={name ?? email ?? undefined}
              size="sm"
              src={avatarUrl}
            />
          </button>
        </DropdownTrigger>
        <DropdownMenu
          aria-label={t("userPages.actions.accountMenu")}
          className="min-w-[220px] max-w-[min(280px,calc(100vw-2rem))]"
          itemClasses={{ base: "min-h-11 sm:min-h-9" }}
          variant="flat"
        >
          <DropdownSection
            showDivider
            aria-label={t("userPages.actions.signedInAs")}
          >
            <DropdownItem
              key="profile"
              isReadOnly
              className="cursor-default gap-1 py-2 data-[hover=true]:bg-transparent"
              textValue={name ?? email ?? t("userPages.actions.signedInAs")}
            >
              <p className="text-xs text-default-500">
                {t("userPages.actions.signedInAs")}
              </p>
              {/* Never recorded by analytics session replays */}
              <div className="min-w-0" data-dd-privacy="mask">
                {name && <p className="truncate font-semibold">{name}</p>}
                {email && (
                  <p className="truncate text-xs text-default-500">{email}</p>
                )}
              </div>
            </DropdownItem>
          </DropdownSection>
          <DropdownSection showDivider aria-label={t("nav.myLibrary")}>
            <DropdownItem
              key="watchlist"
              endContent={<Count value={count(library.watchlist)} />}
              href={`/${lang}/watchlist`}
            >
              {t("userPages.actions.myWatchlist")}
            </DropdownItem>
            <DropdownItem
              key="watched"
              endContent={<Count value={count(library.watched)} />}
              href={`/${lang}/watched`}
            >
              {t("userPages.actions.watchedMovies")}
            </DropdownItem>
            <DropdownItem
              key="ratings"
              endContent={<Count value={count(library.ratings)} />}
              href={`/${lang}/ratings`}
            >
              {t("userPages.actions.myRatings")}
            </DropdownItem>
          </DropdownSection>
          <DropdownSection aria-label={t("userPages.actions.account")}>
            <DropdownItem key="account" href={`/${lang}/account`}>
              {t("userPages.actions.account")}
            </DropdownItem>
            <DropdownItem
              key="logout"
              className="text-danger"
              color="danger"
              onPress={() => void handleSignOut()}
            >
              {t("userPages.actions.signOut")}
            </DropdownItem>
          </DropdownSection>
        </DropdownMenu>
      </Dropdown>
    </AuthSlot>
  );
}
